import { engineComponent, StringUtils, Logger } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Tweens } from "../Components/Effects/Tweens";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { ParticleGroupElement } from "../Components/Elements/Graphics/ParticleGroupElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Configuration } from "../Components/Configuration";
import { PlayList } from "../Components/Sounds/PlayList";

@engineComponent(ComponentTypes.DisplayObject)
/**
 * @author Marek Turkovic
 * 31.10.2018,
 * 5.10.2021 edited by Tomas Mastera
 */
export class WheelElement extends ContainerElement {
    static WHEEL_MULTIPLIERS: string = "Multipliers";
    static WHEEL_FREESPINS: string = "Freespins";
    static WHEEL_LEVELS: string = "Levels";

    private wheelType: string;

    private duration: number = 100;
    private delay: number = 0;
    private delayDefault: number = 0;
    private startIndex: number;
    private stopIndex: number;
    private lastStoppedIndex: number = 0;
    private ease: Array<string> = ["linear", "linear", "linear"];
    private turns: number = 0;

    private values: Object = {};
    private scenes: Object = {};

    private scenesSize: number = 0;

    private scenesOriginalRotations: Array<number> = [];
    private rotationClockwise: boolean = true;

    private delayTimer: FrameTimerEventEmitter;
    private stoppingDelayTimer: FrameTimerEventEmitter;
    private startWheelLoopSoundTimer: FrameTimerEventEmitter;
    private stopWheelLoopSoundTimer: FrameTimerEventEmitter;

    public finished: boolean = true;
    public stopping: boolean = false;

    private _preRollAngle: number = 0;
    private _preRollDuration: number = 0;
    private _postRollAngle: number = 0;
    private _postRollDuration: number = 0;

    private _stoppingDelayOffset: number = 0;

    private isRespin: boolean = false;
    private canStopping: boolean = false;
    private counterRunCycles: number = 0;

    constructor() {
        super();

        this.delayTimer = CreateFrameTimer("delayTimer");
        this.delayTimer.on(FrameTimerEvents.Complete, this._play.bind(this));

        this.stoppingDelayTimer = CreateFrameTimer("stoppingDelayTimer");
        this.stoppingDelayTimer.on(FrameTimerEvents.Complete, this.stoppingEmitDelayed.bind(this));

        this.startWheelLoopSoundTimer = CreateFrameTimer("startWheelLoopSoundTimer");
        this.startWheelLoopSoundTimer.on(
            FrameTimerEvents.Complete,
            function () {
                this.playWheelSound(1);
            }.bind(this),
        );

        this.stopWheelLoopSoundTimer = CreateFrameTimer("stopWheelLoopSoundTimer");
        this.stopWheelLoopSoundTimer.on(
            FrameTimerEvents.Complete,
            function () {
                this.playWheelSound(2);
            }.bind(this),
        );
    }

    /**
     *
     * @param {Element} node
     */
    public createByNode(node: Element): void {
        super.createByNode(node);

        if (node.hasAttribute(WheelElementAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(WheelElementAttributes.CONFIGS).split(";");

            for (let i = 0; i < configs.length; i++) {
                this.configure(Configuration.Configurations[configs[i]]);
            }
        }

        if (node.hasAttribute(WheelElementAttributes.PRE_ROLL_ANGLE)) {
            this._preRollAngle = parseFloat(node.getAttribute(WheelElementAttributes.PRE_ROLL_ANGLE));
        }

        if (node.hasAttribute(WheelElementAttributes.PRE_ROLL_DURATION)) {
            this._preRollDuration = parseInt(node.getAttribute(WheelElementAttributes.PRE_ROLL_DURATION));
        }

        if (node.hasAttribute(WheelElementAttributes.POST_ROLL_ANGLE)) {
            this._postRollAngle = parseFloat(node.getAttribute(WheelElementAttributes.POST_ROLL_ANGLE));
        }

        if (node.hasAttribute(WheelElementAttributes.POST_ROLL_DURATION)) {
            this._postRollDuration = parseInt(node.getAttribute(WheelElementAttributes.POST_ROLL_DURATION));
        }

        if (node.hasAttribute(WheelElementAttributes.STOPPING_DELAY_OFFSET)) {
            this._stoppingDelayOffset = parseInt(node.getAttribute(WheelElementAttributes.STOPPING_DELAY_OFFSET));
        }

        if (node.hasAttribute(WheelElementAttributes.DURATION)) {
            this.duration = parseInt(node.getAttribute(WheelElementAttributes.DURATION));
        }

        if (node.hasAttribute(WheelElementAttributes.DELAY)) {
            this.delay = parseInt(node.getAttribute(WheelElementAttributes.DELAY));
            this.delayDefault = parseInt(node.getAttribute(WheelElementAttributes.DELAY));
        }

        if (node.hasAttribute(WheelElementAttributes.EASE)) {
            this.ease = node.getAttribute(WheelElementAttributes.EASE).split(";");
        }

        if (node.hasAttribute(WheelElementAttributes.TURNS)) {
            this.turns = parseInt(node.getAttribute(WheelElementAttributes.TURNS));
        }

        if (node.hasAttribute(WheelElementAttributes.FROM_SCENE)) {
            this.startIndex = parseInt(node.getAttribute(WheelElementAttributes.FROM_SCENE));
        }

        if (node.hasAttribute(WheelElementAttributes.TO_SCENE)) {
            this.stopIndex = parseInt(node.getAttribute(WheelElementAttributes.TO_SCENE));

            for (let key in this.scenes) {
                if (
                    this.scenes.hasOwnProperty(key) &&
                    this.scenes[key] === node.getAttribute(WheelElementAttributes.TO_SCENE)
                ) {
                    if (this.startIndex == null) {
                        this.setActiveScene(parseInt(key));
                        //this.setRotation();
                    }
                }
            }
        }

        if (
            node.hasAttribute(WheelElementAttributes.PLAY) &&
            StringUtils.parseBoolean(node.getAttribute(WheelElementAttributes.PLAY))
        ) {
            if (this.delay) {
                this.delayTimer.start(this.delay);
            } else {
                this._play();
            }
        }
    }

    /**
     *
     * @param {Element} node
     */
    protected configure(node: Element): void {
        if (node != null) {
            // if (node.nodeName === 'WheelConfiguration') {
            let child: Element = node.firstElementChild;
            while (child != null) {
                if (child.hasAttribute("DataSceneId")) {
                    this.scenes[child.getAttribute("DataSceneId")] = child.nodeName;
                    this.scenesSize++;

                    if (child.hasAttribute("Value")) {
                        this.values[child.getAttribute("DataSceneId")] = parseInt(child.getAttribute("Value"));
                    }

                    this.scenesOriginalRotations[child.getAttribute("DataSceneId")] = parseFloat(
                        child.getAttribute("Rotation"),
                    );
                }

                child = child.nextElementSibling;
            }
            // }
        }
    }

    /**
     *
     */
    public play() {
        this.finished = false;
        this.stopping = false;
        this.isRespin = false;
        this.canStopping = false;
        this.counterRunCycles = 0;

        this.stoppingDelayTimer.stop();
        this.delayTimer.stop();
        if (this.delay) {
            this.delayTimer.start(this.delay);
            // this.delay = 0; //reset delay after call
        } else {
            this._play();
        }
    }

    public playRespin() {
        this.finished = false;
        this.stopping = false;
        this.isRespin = true;
        this.canStopping = false;
        this.counterRunCycles = 0;

        this.stoppingDelayTimer.stop();
        this.delayTimer.stop();
        // if (this.delay) {
        //     this.delayTimer.start(this.delay);
        //     // this.delay = 0; //reset delay after call
        // } else {
        this._play();
        // }
    }

    /**
     *
     * @private
     */
    private _play() {
        // if (this.childs['ColoredSlices'] != null && this.childs['ColoredSlices'] instanceof SwitchElement) {
        //     let elements = this.childs['ColoredSlices'].elements;
        //
        //     for (let i = 0; i < elements.children.length; i++) {
        //         if (elements.children[i] instanceof SwitchElement) {
        //             if ((<SwitchElement>elements.children[i]).getCurrentCase() != 'ACTIVE')
        //                 (<SwitchElement>elements.children[i]).goToCase('ACTIVE');
        //         }
        //     }
        // }

        // wheel start sound
        this.playWheelSound(0);
        // wheel loop sound
        this.startWheelLoopSoundTimer.stop();
        this.startWheelLoopSoundTimer.start(110);

        if (this.isRespin) {
            // respin
            this.preRollWheel(); // preroll
        } else {
            // normal spin
            this.spinWheel(); // preroll + roll + postroll
        }
    }

    /**
     * spin wheel
     */
    private spinWheel(): void {
        if (this.startIndex != this.stopIndex || (this.startIndex == this.stopIndex && this.turns)) {
            let callbacksInTime: { (param: string): void }[] = [];

            // Start
            callbacksInTime[this._preRollDuration] = () => {
                this.emit("wheelRotationStart");
            };

            // duration of WheelStart sound 1.9s = 114 frames
            // callbacksInTime[110] = () => {
            //     // 0 -  wheel start sound, 1 - wheel loop sound, 2 - wheel loop stop and wheel end start sound
            //     this.playWheelSound(1);
            // };

            // duration of WheelEnd sound 1.743s = 104 frames
            callbacksInTime[this.duration + this._preRollDuration - 115] = () => {
                this.playWheelSound(2);
            };

            // Half
            // callbacksInTime[this.duration / 2 - 2 * (this.duration / this.turns)] = () => {
            //     this.emit('wheelRotationHalf');
            // };

            // Stopping
            callbacksInTime[this.duration - this._preRollDuration - this._stoppingDelayOffset] = () => {
                this.stopping = true;
                this.emit("wheelRotationStopping");
            };

            // Commute
            // callbacksInTime[this.duration - (this.duration / this.turns)] = () => {
            //     this.emit('wheelRotationCommute');
            // };

            // End
            callbacksInTime[this.duration] = () => {
                this.finished = true;
                this.emit("wheelRotationComplete");
            };

            Tweens.removeTweens(this, "rotation");
            Tweens.customTween(this, {
                tween: "rotation",
                tweenValues: [
                    -this.scenesOriginalRotations[this.startIndex],
                    -this.scenesOriginalRotations[this.startIndex] +
                        (this.rotationClockwise ? -1 : 1) * this._preRollAngle,
                    (this.rotationClockwise ? 1 : -1) * (this.turns * 360) -
                        this.scenesOriginalRotations[this.stopIndex] +
                        (this.rotationClockwise ? 1 : -1) * this._postRollAngle,
                    (this.rotationClockwise ? 1 : -1) * (this.turns * 360) -
                        this.scenesOriginalRotations[this.stopIndex],
                ],
                tweenDuration: [
                    this._preRollDuration,
                    this.duration - this._preRollDuration - this._postRollDuration,
                    this._postRollDuration,
                ],
                tweenEase: [this.ease[0], this.ease[1], this.ease[2]],
                callbacksInTime: callbacksInTime,
            });
        } else {
            this.rotation = this.degrees2radians(-this.scenesOriginalRotations[this.startIndex]);
        }

        this.lastStoppedIndex = this.stopIndex;
    }

    /**
     * respin wheel - preroll with wheel start (departure)
     */
    private preRollWheel(): void {
        // Start
        this.emit("wheelRotationStart");

        Tweens.removeTweens(this, "rotation");
        Tweens.customTween(this, {
            tween: "rotation",
            tweenValues: [
                -this.scenesOriginalRotations[this.lastStoppedIndex], // current start angle
                -this.scenesOriginalRotations[this.lastStoppedIndex] +
                    (this.rotationClockwise ? -1 : 1) * this._preRollAngle, // current start angle + preRollAngle
                -this.scenesOriginalRotations[this.lastStoppedIndex] +
                    (this.rotationClockwise ? -1 : 1) * this._preRollAngle +
                    (this.rotationClockwise ? 1 : -1) * 180, // current start angle + preRollAngle + starting angle 180
            ],
            tweenDuration: [this._preRollDuration, this.duration / this.turns / 2], // /2 because starting angle is 180 -> 360/2
            tweenEase: ["sineIn", "sineIn"],
            callbackFunction: this.rollWheel,
            callbackScope: this,
        });
    }

    /**
     * respin wheel - roll = infinite rotation
     */
    private rollWheel(): void {
        if (this.canStopping && this.counterRunCycles > 0) {
            // if has data value and was > 0 of cycles
            this.postRollWheel();
        } else {
            Tweens.removeTweens(this, "rotation");

            let currentAngleDeg: number = this.radians2degrees(this.rotation);
            let addAngleDeg: number = currentAngleDeg + (this.rotationClockwise ? 1 : -1) * 360; // current+360 in loop !!!!

            Tweens.customTween(this, {
                tween: "rotation",
                tweenValues: [currentAngleDeg, addAngleDeg],
                tweenDuration: [this.duration / this.turns - this._preRollDuration - this._postRollDuration],
                tweenEase: ["linear"],
                callbackFunction: this.rollWheel,
                callbackScope: this,
                // tweenLoop: true // LOOP !!!!!!!!!!!!!!!
            });

            this.counterRunCycles++;
        }
    }

    /**
     * respin wheel - postroll
     */
    private postRollWheel(): void {
        Tweens.removeTweens(this, "rotation");

        let currentAngleDeg: number = this.radians2degrees(this.rotation);
        currentAngleDeg = currentAngleDeg % 360; // update angle in range 0-360°

        let stopAngleDeg: number =
            (this.rotationClockwise ? 2 : -2) * 360 - this.scenesOriginalRotations[this.stopIndex]; // WARNING this add 2*360 to stop angle, because stop angle cam be between 0-360° and result angle not be < 0
        let rollDuration: number = Math.round(
            (this.duration / this.turns) * ((Math.abs(stopAngleDeg) - Math.abs(currentAngleDeg)) / 360),
        );

        this.stoppingDelayTimer.stop();
        this.stoppingDelayTimer.start(
            rollDuration - this._stoppingDelayOffset > 0 ? rollDuration - this._stoppingDelayOffset : 0,
        );

        this.stopWheelLoopSoundTimer.stop();
        this.stopWheelLoopSoundTimer.start(rollDuration + this._postRollDuration - 115);

        Tweens.customTween(this, {
            tween: "rotation",
            tweenValues: [
                currentAngleDeg,
                stopAngleDeg + (this.rotationClockwise ? 1 : -1) * this._postRollAngle,
                stopAngleDeg,
            ],
            tweenDuration: [rollDuration, this._postRollDuration],
            tweenEase: ["sineOut", "sineInOut"],
            callbackFunction: this.respinRollFinished,
            callbackScope: this,
        });
    }

    private playWheelSound(soundType: number): void {
        if (soundType == 0) {
            PlayList.loop("SND_FSWheelDeepLoop", 15);
            if (this.wheelType === WheelElement.WHEEL_FREESPINS) {
                PlayList.play("SND_FSWheelFreespinsStart");
            } else if (this.wheelType === WheelElement.WHEEL_MULTIPLIERS) {
                PlayList.play("SND_FSWheelMultipliersStart");
            } else if (this.wheelType === WheelElement.WHEEL_LEVELS) {
                PlayList.play("SND_FSWheelLevelsStart");
            }
        } else if (soundType == 1) {
            if (this.wheelType === WheelElement.WHEEL_FREESPINS) {
                PlayList.loop("SND_FSWheelFreespinsLoop", 5);
            } else if (this.wheelType === WheelElement.WHEEL_MULTIPLIERS) {
                PlayList.loop("SND_FSWheelMultipliersLoop", 5);
            } else if (this.wheelType === WheelElement.WHEEL_LEVELS) {
                PlayList.loop("SND_FSWheelLevelsLoop", 5);
            }
        } else if (soundType == 2) {
            if (this.wheelType === WheelElement.WHEEL_FREESPINS) {
                PlayList.stop("SND_FSWheelFreespinsLoop", 10);
                PlayList.play("SND_FSWheelFreespinsEnd", 5);
            } else if (this.wheelType === WheelElement.WHEEL_MULTIPLIERS) {
                PlayList.stop("SND_FSWheelMultipliersLoop", 10);
                PlayList.play("SND_FSWheelMultipliersEnd", 5);
            } else if (this.wheelType === WheelElement.WHEEL_LEVELS) {
                PlayList.stop("SND_FSWheelLevelsLoop", 10);
                PlayList.play("SND_FSWheelLevelsEnd", 5);
            }
        }
    }

    private stoppingEmitDelayed(): void {
        this.stoppingDelayTimer.stop();
        this.stopping = true;
        this.emit("wheelRotationStopping");
    }

    private respinRollFinished(): void {
        Tweens.removeTweens(this, "rotation");

        this.finished = true;

        this.rotation = this.degrees2radians(-this.scenesOriginalRotations[this.stopIndex]);
        this.lastStoppedIndex = this.stopIndex;

        this.emit("wheelRotationComplete");
    }

    public stopRespin(value: number) {
        this.canStopping = true;
        this.stopIndex = this.getSceneByValue(value);
    }

    /**
     * Set rotation of wheel.
     */
    public setRotation(): void {
        this.rotation = this.degrees2radians(-this.scenesOriginalRotations[this.startIndex]);
    }

    public setType(wheelType: string): void {
        this.wheelType = wheelType;
    }

    /**
     *
     * @returns {number}
     */
    public getLastStoppedIndex(): number {
        return this.lastStoppedIndex;
    }

    /**
     *
     * @param from  Scene identificator set in DataId.
     * @param to    Scene identificator set in DataId.
     * @param {number} duration
     * @param delay
     * @param clockwise
     */
    public goToScene(from: number, to: number, duration?: number, delay?: number, clockwise?: boolean): WheelElement {
        if (from != null) {
            this.startIndex = from;
        } else {
            this.startIndex = this.lastStoppedIndex;
        }

        if (to != null) {
            this.stopIndex = to;
        }

        if (duration != null) {
            this.duration = duration;
        }

        if (delay != null) {
            this.delay = delay;
        }

        if (clockwise != null) {
            this.rotationClockwise = clockwise;
        }

        if (from != null) {
            if (DEBUG) {
                this.log(
                    "WheelElement: new " +
                        this.startIndex +
                        this.scenes[from] +
                        " to " +
                        this.scenes[to] +
                        " during " +
                        duration +
                        " with delay " +
                        delay,
                );
            }
        } else {
            if (DEBUG) {
                //this.log('WheelElement: last index ' + this.startIndex + ' : ' + this.children[this.startIndex].name + ' to ' + this.scenes[to] + ' during ' + duration + ' with delay ' + delay);
            }
        }

        return this;
    }

    /**
     *
     * @param {number} to   Scene identificator set in DataId.
     * @returns {WheelElement}
     */
    public setActiveScene(to: number): WheelElement {
        if (to < 0 && this.scenesSize > 0) {
            to = Math.round(this.getRandom(1, this.scenesSize));
        }

        this.startIndex = this.stopIndex = to;
        this.lastStoppedIndex = this.stopIndex;

        // ParticleGroupElement.DynamicKey = this.scenes[to];

        if (DEBUG) {
            this.log("WheelElement: Scene set to (" + this.startIndex + ") " + this.scenes[to]);
        }

        return this;
    }

    /**
     *
     * @param turns
     */
    public setTurns(turns: number): WheelElement {
        if (turns == null) {
            return this;
        }

        this.turns = turns;

        if (DEBUG) {
            this.log("WheelElement: Turns set to (" + this.turns + ") ");
        }

        return this;
    }

    /**
     * Return random selected scene index by queried value.
     * If no value is queried, random scene index of all values is returned.
     *
     * @param {number} value
     * @returns {number}
     */
    public getSceneByValue(value: number = null): number {
        let groupOfSelectedValues: Array<number> = [];

        for (let key in this.values) {
            if (value != null && value != 0) {
                if (this.values[key] === value) {
                    groupOfSelectedValues.push(parseInt(key));
                }
            } else {
                groupOfSelectedValues.push(parseInt(key));
            }
        }

        if (groupOfSelectedValues.length == 0) {
            // if not find value, return first element
            return this.values[1];
        }

        return groupOfSelectedValues[Math.floor(Math.random() * (groupOfSelectedValues.length - 1))];
    }

    public getDelayDefault(): number {
        return this.delayDefault;
    }

    /**
     *
     * @param {Array<string>} ease
     * @returns {WheelElement}
     */
    public setEase(ease: Array<string> = ["linear", "linear", "linear"]): WheelElement {
        this.ease = ease;
        return this;
    }

    /**
     * Present selected slice by custom action.
     */
    public presentSelectedSlice(): void {
        // if (this.childs['ColoredSlices'] != null) {
        //     if (this.childs['ColoredSlices'] instanceof ContainerElement) {
        //         (this.childs['ColoredSlices'].childs['Slice' + this.stopIndex] as SwitchElement).goToCase('WIN');
        //     } else if (this.childs['ColoredSlices'] instanceof SwitchElement) {
        //
        //         let elements = this.childs['ColoredSlices'].elements;
        //         (elements.childs['Slice' + this.stopIndex] as SwitchElement).goToCase('WIN');
        //
        //         for (let i = 0; i < elements.children.length; i++) {
        //             if (elements.children[i] instanceof SwitchElement) {
        //                 if ((<SwitchElement>elements.children[i]).getCurrentCase() != 'WIN')
        //                     (<SwitchElement>elements.children[i]).goToCase('NOWIN');
        //             }
        //         }
        //     }
        // }
    }

    /**
     *
     * @returns {WheelElement}
     */
    // public activate(delay?: number): WheelElement
    // {
    //
    //     let leftmost = (this.stopIndex - 3);
    //     if (leftmost < 1) {
    //         leftmost = 12 + leftmost;
    //     }
    //
    //     // 7 visible slices
    //     for (let c = 0; c < 7; c++) {
    //         let i = leftmost + c;
    //         if (i > 12) {
    //             i -= 12;
    //         }
    //
    //         if (this.childs['ColoredSlices'] != null) {
    //             if (this.childs['ColoredSlices'] instanceof SwitchElement) {
    //                 this.childs['ColoredSlices'].goToCase('ACTIVE');
    //
    //                 let elements = this.childs['ColoredSlices'].elements;
    //                 if (elements.childs['Slice' + i] instanceof SwitchElement) {
    //                     (<SwitchElement>elements.childs['Slice' + i]).setDynamicTime(delay + (c * 10)).goToCase('ACTIVATE_DELAYED');
    //                 }
    //             }
    //         }
    //     }
    //
    //     return this;
    // }

    /**
     *
     * @returns {WheelElement}
     */
    // public inactivate(): WheelElement
    // {
    //     if (this.childs['ColoredSlices'] != null) {
    //         if (this.childs['ColoredSlices'] instanceof SwitchElement) {
    //             this.childs['ColoredSlices'].goToCase('INACTIVE');
    //         }
    //     }
    //
    //     return this;
    // }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public radians2degrees(radians: number): number {
        return radians * (180 / Math.PI);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }
}

const WheelElementAttributes = {
    /**
     *
     */
    CONFIGS: "Configs",
    /**
     *
     */
    DELAY: "Delay",
    /**
     *
     */
    DURATION: "Duration",
    /**
     *
     */
    EASE: "Ease",
    /**
     *
     */
    FROM_SCENE: "FromScene",
    /**
     *
     */
    PLAY: "Play",
    /**
     *
     */
    TO_SCENE: "ToScene",
    /**
     *
     */
    TURNS: "Turns",
    /**
     *
     */
    PRE_ROLL_ANGLE: "PreRollAngle",
    /**
     *
     */
    PRE_ROLL_DURATION: "PreRollDuration",
    /**
     *
     */
    POST_ROLL_ANGLE: "PostRollAngle",
    /**
     *
     */
    POST_ROLL_DURATION: "PostRollDuration",
    /**
     * this can be negative number
     */
    STOPPING_DELAY_OFFSET: "StoppingDelayOffset",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WheelElement.ts
 **/